import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, throwError, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { Attachment } from '@digital-platform/attachments/domain';

@Injectable({
  providedIn: 'root',
})
export class AttachmentUploadService {
  private API_BASE_URL = '/api/attachments';

  constructor(private httpClient: HttpClient) {}

  public find(id: string, name: string): Observable<Attachment> {
    return this.httpClient.get<Attachment>(
      `${this.API_BASE_URL}/${id}/${name}`
    );
  }

  public del(attachment: Attachment): Observable<Attachment> {
    const fileName = attachment?.fileUrl.split('/').pop();
    if (!attachment?.id) {
      // legacy data and/or a dummy attachment file, delete it
      return of(attachment);
    }

    return this.httpClient
      .delete<Attachment>(`${this.API_BASE_URL}/${attachment.id}/${fileName}`)
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            // it's already been deleted, or didn't exist, so we can treat this as a success
            return of(attachment);
          }

          return throwError(error);
        })
      );
  }

  public create(file: File): Observable<HttpEvent<any>> {
    const attachment: Attachment = {
      id: null,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      fileUrl: '',
      uploadDate: new Date(),
    };

    return this.httpClient
      .post<Attachment>(`${this.API_BASE_URL}`, attachment)
      .pipe(
        switchMap((attachmentDetails) => {
          if (!attachmentDetails.fileUrl) {
            throw new Error('Error retrieving upload URL');
          }

          const { fileUrl, ...createdAttachment } = attachmentDetails;

          const options = {
            reportProgress: true,
          };

          const req = new HttpRequest<Blob>('PUT', fileUrl, file, options);
          return this.httpClient.request(req).pipe(
            map((event) => {
              if (
                event.type !== HttpEventType.Response ||
                event.status !== 200
              ) {
                return event;
              }

              return new HttpResponse<Attachment>({
                ...event,
                body: {
                  ...createdAttachment,
                  fileUrl: `${this.API_BASE_URL}/${createdAttachment.id}/${createdAttachment.fileName}`,
                },
              });
            })
          );
        })
      );
  }
}
